import { useMemo, useState } from 'react';
import { differenceInMinutes } from '@repo/date-time';
import type { Price, Reservation } from '@repo/domain';
import {
  BusinessValidationCode,
  BusinessValidationError,
  formatFlatPrice,
  useReservationActions,
} from '@repo/domain-service';
import { TestId } from '@repo/test-id';
import { useNavigate } from 'react-router-dom';
import { i18n, tr } from '@repo/locale';

import SummaryContents from '@features/reservation/components/summary/SummaryContents';
import VehicleChangeContainer from '@features/reservation/components/vehicle/VehicleChangeContainer';
import { getReservationParkTimeContract } from '@features/reservation/utils/getReservationParkTimeContract';
import { handleReservationError } from '@features/reservation/utils/handleReservationError';
import { trackReservationSuccess } from '@features/reservation/trackers/trackReservationResponse';

import PageFrame from '@shared/components/page/PageFrame';
import { useParkingSearchStore } from '@shared/stores/useParkingSearchStore';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

interface Props {
  reservation: Reservation;
  vehicleSelectionOpen: boolean;
  onVehicleSelectionOpenChange: (open: boolean) => void;
  secondsToDeadline?: number;
  onExpire?: () => void;
}

export default function ReservationSummaryContents({
  reservation,
  vehicleSelectionOpen,
  onVehicleSelectionOpenChange,
  secondsToDeadline,
  onExpire,
}: Props) {
  const testId = TestId.root('reservation.summaryPage');
  const navigate = useNavigate();
  const { initiateReservationPayment } = useReservationActions();
  const [confirmingFree, setConfirmingFree] = useState<boolean>(false);

  const { parking, vehicle } = reservation;
  const reservationVehicles = reservation.vehicles ?? (vehicle ? [vehicle] : []);
  const parkDuration = differenceInMinutes(reservation.endAt, reservation.startAt);
  const parkTimeContract = getReservationParkTimeContract(reservation);

  const flatPrice = reservation.estimatedPrice ?? reservation.price;
  const price: Price | undefined =
    parking && flatPrice
      ? { id: reservation.id, parkingId: parking.id, parkTimeContract, flatPrice, estimated: true }
      : undefined;
  const lastPriceEstimate = useParkingSearchStore((state) => state.lastPriceEstimate);
  const previousFlatPrice =
    lastPriceEstimate &&
    flatPrice &&
    lastPriceEstimate.parkingId === reservation.parkingId &&
    (lastPriceEstimate.flatPrice.amount !== flatPrice.amount ||
      lastPriceEstimate.flatPrice.currency !== flatPrice.currency)
      ? lastPriceEstimate.flatPrice
      : undefined;
  // The free parking time pays for the whole reservation: it is confirmed here, with no payment step.
  const isFree = flatPrice?.amount === 0;
  const confirmButtonLabel = useMemo(() => {
    if (isFree) return tr('reservations:summary.confirm_free');
    const formattedTotalPrice = flatPrice ? formatFlatPrice(flatPrice, i18n.language) : undefined;
    return `${tr('reservations:summary.confirm_and_pay')} • ${formattedTotalPrice}`;
  }, [flatPrice, isFree]);

  const confirmFreeReservation = async () => {
    setConfirmingFree(true);
    try {
      const response = await initiateReservationPayment({ reservationId: reservation.id });
      trackReservationSuccess({ paymentMethod: undefined, response });
      navigate(ROUTE_PATHS[RouteName.RESERVATION_SUCCESS], { replace: true });
    } catch (error) {
      setConfirmingFree(false);
      // The free time was used up meanwhile, so the reservation costs something: pay for it as usual.
      if (error instanceof BusinessValidationError && error.code === BusinessValidationCode.paymentMethodRequired) {
        navigate(ROUTE_PATHS[RouteName.RESERVATION_PAYMENT]);
        return;
      }
      handleReservationError(error, navigate, undefined);
    }
  };

  const handleConfirm = () => {
    if (!isFree) {
      navigate(ROUTE_PATHS[RouteName.RESERVATION_PAYMENT]);
      return;
    }
    if (confirmingFree) return;
    void confirmFreeReservation();
  };

  const handleOpenVehicleSelection = () => {
    onVehicleSelectionOpenChange(true);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (!parking || !vehicle) return null;

  return (
    <PageFrame
      testId={testId}
      headerText={tr('reservations:summary.title')}
      onGoBack={handleGoBack}
      forwardButton={{
        label: confirmButtonLabel,
        pending: confirmingFree,
        onClick: handleConfirm,
      }}
      drawer={
        <VehicleChangeContainer
          testId={testId}
          reservation={reservation}
          open={vehicleSelectionOpen}
          onOpenChange={onVehicleSelectionOpenChange}
        />
      }
    >
      <SummaryContents
        parking={parking}
        price={price}
        previousFlatPrice={previousFlatPrice}
        testId={testId}
        onVehicleChange={handleOpenVehicleSelection}
        vehicle={vehicle}
        vehicles={reservationVehicles}
        spotName={reservation.spot?.name}
        startAt={reservation.startAt}
        parkDuration={parkDuration}
        secondsToDeadline={secondsToDeadline}
        onExpire={onExpire}
      />
    </PageFrame>
  );
}
